import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ChefHat, Plus, Trash2, Edit, Calculator } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;
const CATEGORIAS = ["platillo", "bebida", "snack", "postre", "salsa", "base"];

const emptyReceta = {
  nombre: "", categoria: "platillo", porciones_base: 1,
  ingredientes: [], subrecetas: [], margen_pct: 30,
  tiempo_prep_min: 0, instrucciones: "", notas: "",
};
const emptyIng = { producto: "", cantidad: 0, unidad: "kg", costo_unitario: 0, merma_pct: 0, inventario_item_id: "" };

const calcCosto = (r) => {
  let costo = 0;
  for (const i of r.ingredientes || []) {
    const q = Number(i.cantidad) || 0;
    const c = Number(i.costo_unitario) || 0;
    const m = Number(i.merma_pct) || 0;
    costo += q * c * (1 + m / 100);
  }
  return costo;
};

export default function RecetasTab({ unidadSlug, color }) {
  const [recetas, setRecetas] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyReceta);

  // Escalador
  const [escOpen, setEscOpen] = useState(false);
  const [escReceta, setEscReceta] = useState(null);
  const [escPorciones, setEscPorciones] = useState(50);
  const [escData, setEscData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?unidad_negocio=${unidadSlug}`;
      const [r, inv] = await Promise.all([
        axios.get(`${API}/recetas${q}`),
        axios.get(`${API}/inventario/items${q}`).catch(() => ({ data: [] })),
      ]);
      setRecetas(r.data || []);
      setInventario(inv.data || []);
    } catch { toast.error("Error al cargar recetas"); }
    finally { setLoading(false); }
  }, [unidadSlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm(emptyReceta); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyReceta, ...r }); setOpen(true); };

  const handleSave = async () => {
    if (!form.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = {
        ...form,
        porciones_base: Math.max(Number(form.porciones_base) || 1, 1),
        tiempo_prep_min: Number(form.tiempo_prep_min) || 0,
        margen_pct: Number(form.margen_pct) || 0,
        unidad_negocio: unidadSlug,
        ingredientes: (form.ingredientes || []).map((i) => ({
          ...i,
          cantidad: Number(i.cantidad) || 0,
          costo_unitario: Number(i.costo_unitario) || 0,
          merma_pct: Number(i.merma_pct) || 0,
          inventario_item_id: i.inventario_item_id || null,
        })),
        subrecetas: (form.subrecetas || []).map((s) => ({
          receta_id: s.receta_id,
          porciones: Number(s.porciones) || 1,
        })),
      };
      if (editing) {
        await axios.put(`${API}/recetas/${editing.receta_id}`, payload);
        toast.success("Receta actualizada");
      } else {
        await axios.post(`${API}/recetas`, payload);
        toast.success("Receta creada");
      }
      setOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar receta?")) return;
    try {
      await axios.delete(`${API}/recetas/${id}`);
      toast.success("Eliminada");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const addIng = () => setForm({ ...form, ingredientes: [...(form.ingredientes || []), { ...emptyIng }] });
  const updIng = (idx, field, val) => {
    const arr = [...(form.ingredientes || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    // Autocompletar desde inventario
    if (field === "inventario_item_id" && val) {
      const inv = inventario.find((x) => x.item_id === val);
      if (inv) {
        arr[idx].producto = inv.nombre;
        arr[idx].unidad = inv.unidad;
        arr[idx].costo_unitario = inv.costo_promedio || 0;
      }
    }
    setForm({ ...form, ingredientes: arr });
  };
  const delIng = (idx) => setForm({ ...form, ingredientes: form.ingredientes.filter((_, i) => i !== idx) });

  const addSub = () => setForm({ ...form, subrecetas: [...(form.subrecetas || []), { receta_id: "", porciones: 1 }] });
  const updSub = (idx, field, val) => {
    const arr = [...(form.subrecetas || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    setForm({ ...form, subrecetas: arr });
  };
  const delSub = (idx) => setForm({ ...form, subrecetas: form.subrecetas.filter((_, i) => i !== idx) });

  const openEsc = (r) => {
    setEscReceta(r);
    setEscPorciones(r.porciones_base || 50);
    setEscData(null);
    setEscOpen(true);
  };

  const calcularEscalado = async () => {
    if (!escReceta) return;
    try {
      const r = await axios.get(`${API}/recetas/${escReceta.receta_id}/escalar?porciones=${escPorciones}`);
      setEscData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al calcular"); }
  };

  const costoActual = calcCosto(form);
  const porc = Math.max(Number(form.porciones_base) || 1, 1);
  const costoPorPorcion = costoActual / porc;
  const margen = Number(form.margen_pct) || 0;
  const precioSugerido = costoPorPorcion * (1 + margen / 100);

  const recetasParaSub = recetas.filter((r) => !editing || r.receta_id !== editing.receta_id);

  return (
    <div className="space-y-4" data-testid={`recetas-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Recetas</h2>
          <p className="text-stone-500 text-sm mt-1">Costeo por porción · escalado dinámico · sub-recetas.</p>
        </div>
        <Button onClick={openNew} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-nueva-receta">
          <Plus className="w-4 h-4 mr-2" /> Nueva receta
        </Button>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm">Cargando...</div>
      ) : recetas.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-8 text-center text-stone-500">
            <ChefHat className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-sm">Sin recetas registradas.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {recetas.map((r) => {
            const c = calcCosto(r);
            const p = Math.max(Number(r.porciones_base) || 1, 1);
            return (
              <Card key={r.receta_id} className="border-stone-200" data-testid={`card-receta-${r.receta_id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h3 className="font-display text-lg text-stone-900 truncate">{r.nombre}</h3>
                      <p className="text-xs text-stone-500 capitalize">{r.categoria} · {r.porciones_base} porciones</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEsc(r)} title="Escalar" data-testid={`btn-escalar-${r.receta_id}`}><Calculator className="w-4 h-4 text-blue-700" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`btn-edit-receta-${r.receta_id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.receta_id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </div>
                  <p className="text-xs text-stone-600">{(r.ingredientes || []).length} ing · {(r.subrecetas || []).length} sub-recetas</p>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-stone-100">
                    <div><p className="text-xs text-stone-500">Costo total</p><p className="text-sm font-semibold">{fmt(c)}</p></div>
                    <div><p className="text-xs text-stone-500">Por porción</p><p className="text-sm font-semibold" style={{ color }}>{fmt(c / p)}</p></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog editar/crear */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-receta">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar receta" : "Nueva receta"}</DialogTitle>
            <DialogDescription className="text-xs">Ingredientes + sub-recetas + margen. Costo se calcula automáticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="receta-nombre" />
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Porciones base</Label>
                <Input type="number" min="1" value={form.porciones_base} onChange={(e) => setForm({ ...form, porciones_base: e.target.value })} data-testid="receta-porciones" />
              </div>
              <div>
                <Label>Margen % (para precio)</Label>
                <Input type="number" step="0.5" value={form.margen_pct} onChange={(e) => setForm({ ...form, margen_pct: e.target.value })} data-testid="receta-margen" />
              </div>
              <div>
                <Label>Tiempo prep. (min)</Label>
                <Input type="number" value={form.tiempo_prep_min} onChange={(e) => setForm({ ...form, tiempo_prep_min: e.target.value })} />
              </div>
            </div>

            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Ingredientes</Label>
                <Button size="sm" variant="outline" onClick={addIng} data-testid="btn-add-ingrediente"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
              </div>
              {(form.ingredientes || []).length === 0 ? (
                <p className="text-stone-500 text-xs">Sin ingredientes.</p>
              ) : (
                <div className="space-y-2">
                  {form.ingredientes.map((ing, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-stone-200">
                      <div className="col-span-12 sm:col-span-3">
                        <Label className="text-xs">Vincular a inventario</Label>
                        <Select value={ing.inventario_item_id || "__none__"} onValueChange={(v) => updIng(idx, "inventario_item_id", v === "__none__" ? "" : v)}>
                          <SelectTrigger data-testid={`ing-inv-${idx}`}><SelectValue placeholder="Manual" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Manual (sin stock)</SelectItem>
                            {inventario.map((x) => <SelectItem key={x.item_id} value={x.item_id}>{x.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <Label className="text-xs">Producto</Label>
                        <Input value={ing.producto} onChange={(e) => updIng(idx, "producto", e.target.value)} />
                      </div>
                      <div className="col-span-3 sm:col-span-1">
                        <Label className="text-xs">Cant.</Label>
                        <Input type="number" step="0.01" value={ing.cantidad} onChange={(e) => updIng(idx, "cantidad", e.target.value)} data-testid={`ing-cantidad-${idx}`} />
                      </div>
                      <div className="col-span-3 sm:col-span-1">
                        <Label className="text-xs">Unidad</Label>
                        <Input value={ing.unidad} onChange={(e) => updIng(idx, "unidad", e.target.value)} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs">$/unidad</Label>
                        <Input type="number" step="0.01" value={ing.costo_unitario} onChange={(e) => updIng(idx, "costo_unitario", e.target.value)} data-testid={`ing-costo-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-1">
                        <Label className="text-xs">Merma%</Label>
                        <Input type="number" step="0.1" value={ing.merma_pct} onChange={(e) => updIng(idx, "merma_pct", e.target.value)} />
                      </div>
                      <div className="col-span-4 sm:col-span-1 text-right">
                        <Button size="sm" variant="ghost" onClick={() => delIng(idx)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border border-stone-200 rounded-lg p-3 bg-blue-50/40">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Sub-recetas (recetas base incluidas)</Label>
                <Button size="sm" variant="outline" onClick={addSub} data-testid="btn-add-subreceta"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
              </div>
              {(form.subrecetas || []).length === 0 ? (
                <p className="text-stone-500 text-xs">Sin sub-recetas. Ej: salsa base, masa, caldo.</p>
              ) : (
                <div className="space-y-2">
                  {form.subrecetas.map((s, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-blue-200">
                      <div className="col-span-8 sm:col-span-8">
                        <Label className="text-xs">Receta base</Label>
                        <Select value={s.receta_id || "__none__"} onValueChange={(v) => updSub(idx, "receta_id", v === "__none__" ? "" : v)}>
                          <SelectTrigger data-testid={`sub-receta-${idx}`}><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Seleccionar…</SelectItem>
                            {recetasParaSub.map((rr) => <SelectItem key={rr.receta_id} value={rr.receta_id}>{rr.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 sm:col-span-3">
                        <Label className="text-xs">Porciones</Label>
                        <Input type="number" min="0.01" step="0.1" value={s.porciones} onChange={(e) => updSub(idx, "porciones", e.target.value)} data-testid={`sub-porciones-${idx}`} />
                      </div>
                      <div className="col-span-1 text-right">
                        <Button size="sm" variant="ghost" onClick={() => delSub(idx)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 border rounded-lg p-3" style={{ background: `${color}10`, borderColor: `${color}40` }}>
              <div>
                <p className="text-xs text-stone-600">Costo total</p>
                <p className="text-lg font-semibold text-stone-900">{fmt(costoActual)}</p>
              </div>
              <div>
                <p className="text-xs text-stone-600">Costo por porción</p>
                <p className="text-lg font-semibold" style={{ color }}>{fmt(costoPorPorcion)}</p>
              </div>
              <div>
                <p className="text-xs text-stone-600">Precio sugerido (+{margen}%)</p>
                <p className="text-lg font-semibold text-emerald-700" data-testid="receta-precio-sugerido">{fmt(precioSugerido)}</p>
              </div>
            </div>

            <div>
              <Label>Instrucciones</Label>
              <Textarea rows={3} value={form.instrucciones || ""} onChange={(e) => setForm({ ...form, instrucciones: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-guardar-receta">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalador */}
      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-escalar">
          <DialogHeader>
            <DialogTitle>Escalar receta: {escReceta?.nombre}</DialogTitle>
            <DialogDescription className="text-xs">Calcula ingredientes y costo total para N porciones (incluye sub-recetas).</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3 mb-3">
            <div className="flex-1">
              <Label>Porciones objetivo</Label>
              <Input type="number" min="1" value={escPorciones} onChange={(e) => setEscPorciones(Number(e.target.value) || 1)} data-testid="esc-porciones" />
            </div>
            <Button onClick={calcularEscalado} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-calcular-escalado">
              <Calculator className="w-4 h-4 mr-2" /> Calcular
            </Button>
          </div>
          {escData && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-stone-50 rounded-lg p-3"><p className="text-xs text-stone-500">Costo total</p><p className="font-display text-2xl">{fmt(escData.costo_total)}</p></div>
                <div className="rounded-lg p-3" style={{ background: `${color}15` }}><p className="text-xs text-stone-500">Costo por porción</p><p className="font-display text-2xl" style={{ color }}>{fmt(escData.costo_por_porcion)}</p></div>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead><tr className="bg-stone-50 text-xs text-stone-500 text-left"><th className="p-2">Ingrediente</th><th className="p-2 text-right">Cant.</th><th className="p-2">Unidad</th><th className="p-2 text-right">$/unid</th><th className="p-2">Origen</th></tr></thead>
                  <tbody>
                    {escData.ingredientes_expandidos.map((i, idx) => (
                      <tr key={idx} className="border-t border-stone-100" data-testid={`esc-ing-row-${idx}`}>
                        <td className="p-2">{i.producto}</td>
                        <td className="p-2 text-right">{i.cantidad}</td>
                        <td className="p-2">{i.unidad}</td>
                        <td className="p-2 text-right">{fmt(i.costo_unitario)}</td>
                        <td className="p-2 text-xs text-stone-500">{i.origen_receta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
